import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const PAGE_SIZE = 10;
const API = '/api';

const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    refresh: 'M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10 M1 14l4.64 4.36A9 9 0 0020.49 15',
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
    link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71 M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    history: 'M3 3v5h5 M3.05 13A9 9 0 1 0 6 5.3L3 8 M12 7v5l4 2',
    alert: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 17c-.77 1.333.192 3 1.732 3z',
};

const defaultForm = {
    driverId: '',
    vehicleId: '',
    isActive: true,
    notes: ''
};

export default function DriverVehicleMapping({ user, operatorDetails, onNavigate }) {
    const [mappings, setMappings] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [, setHistoryKey] = useState(0);
    // Assignment history lives on its own page (#driver-assignment-history[/driverId])
    const openHistory = (driverId) => {
        if (onNavigate) onNavigate('driver-assignment-history', driverId || null);
        else window.location.hash = driverId ? `driver-assignment-history/${driverId}` : 'driver-assignment-history';
    };
    const changedBy = user?.name || user?.fullName || user?.email || '';

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const operatorId = user?.operatorId || user?._id || 'all';
            
            // 1. Fetch Mappings
            const mappingsRes = await axios.get(`${API}/driver-vehicle-mappings/${operatorId}`);
            setMappings(mappingsRes.data);

            // 2. Fetch Drivers
            const driversRes = await axios.get(`/api/drivers/${operatorId}`);
            setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);

            // 3. Fetch Vehicles
            const vehiclesRes = await axios.get(`${API}/vehicles/${operatorId}`);
            setVehicles(Array.isArray(vehiclesRes.data) ? vehiclesRes.data : []);

        } catch (err) {
            console.error('Fetch mappings error:', err);
            showToast('Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [user]);

    // Unmapped active drivers — exclude drivers with an ACTIVE mapping AND drivers on leave today
    const availableDrivers = useMemo(() => {
        const todayStr = formatDateLocal(new Date());

        const activelyMappedDriverIds = mappings
            .filter(m => m._id !== editingId && m.isActive)
            .map(m => m.driverId?._id || m.driverId);

        return drivers.filter(d => {
            // Must be Active status
            if (d.status !== 'Active') return false;
            // Must not already have an active mapping
            if (activelyMappedDriverIds.includes(d._id)) return false;
            // Must not be on an approved leave today (static flag)
            if (d.status === 'On Leave') return false;
            // Must not have a dynamic approved leave covering today
            const onLeaveToday = d.leaves?.some(l =>
                l.status === 'Approved' &&
                l.startDate?.split('T')[0] <= todayStr &&
                l.endDate?.split('T')[0] >= todayStr
            );
            return !onLeaveToday;
        });
    }, [drivers, mappings, editingId]);

    // Unmapped active vehicles — only exclude vehicles that have an ACTIVE mapping
    // whose driver is NOT on leave today (either by static status or active approved leave)
    const availableVehicles = useMemo(() => {
        const todayStr = formatDateLocal(new Date());

        const activelyMappedVehicleIds = mappings
            .filter(m => {
                if (m._id === editingId || !m.isActive) return false;
                // Static status check
                if (m.driverId?.status === 'On Leave') return false;
                // Dynamic leave check: is the driver on an approved leave today?
                const driverRecord = drivers.find(d => d._id === (m.driverId?._id || m.driverId));
                const onLeaveToday = driverRecord?.leaves?.some(l =>
                    l.status === 'Approved' &&
                    l.startDate?.split('T')[0] <= todayStr &&
                    l.endDate?.split('T')[0] >= todayStr
                );
                return !onLeaveToday; // keep blocking only if NOT on leave today
            })
            .map(m => m.vehicleId?._id || m.vehicleId);

        return vehicles.filter(v =>
            v.vehicleStatus === 'Active' &&
            !activelyMappedVehicleIds.includes(v._id)
        );
    }, [vehicles, mappings, editingId, drivers]);

    const stats = useMemo(() => {
        const activeMapped = mappings.filter(m => m.isActive).length;
        const totalDrivers = drivers.length;
        const unmappedDrivers = drivers.filter(d => 
            !mappings.some(m => (m.driverId?._id || m.driverId) === d._id)
        ).length;
        const unmappedVehicles = vehicles.filter(v => 
            !mappings.some(m => (m.vehicleId?._id || m.vehicleId) === v._id)
        ).length;

        return {
            totalMapped: mappings.length,
            activeMapped,
            unmappedDrivers,
            unmappedVehicles
        };
    }, [mappings, drivers, vehicles]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return mappings.filter(m => {
            if (!q) return true;
            const driverName = m.driverId?.name || '';
            const driverEmail = m.driverId?.email || '';
            const plate = m.vehicleId?.plateNumber || '';
            const make = m.vehicleId?.make || '';
            const model = m.vehicleId?.model || '';
            const notes = m.notes || '';

            return driverName.toLowerCase().includes(q) ||
                driverEmail.toLowerCase().includes(q) ||
                plate.toLowerCase().includes(q) ||
                make.toLowerCase().includes(q) ||
                model.toLowerCase().includes(q) ||
                notes.toLowerCase().includes(q);
        });
    }, [mappings, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.driverId || !form.vehicleId) {
            showToast('Please select both driver and vehicle', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const operatorId = user?.operatorId || user?._id;
            const payload = {
                ...form,
                operatorId,
                changedBy
            };

            if (editingId) {
                const res = await axios.put(`${API}/driver-vehicle-mappings/${editingId}`, payload);
                showToast('Mapping updated successfully!');
            } else {
                const res = await axios.post(`${API}/driver-vehicle-mappings`, payload);
                showToast('Driver-Vehicle mapping established successfully!');
            }
            
            fetchData();
            setHistoryKey(k => k + 1);
            closeModal();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save mapping';
            showToast(msg, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (mapping) => {
        const driverName = mapping.driverId?.name || 'Driver';
        const plate = mapping.vehicleId?.plateNumber || 'Vehicle';
        if (!window.confirm(`Are you sure you want to unmap driver "${driverName}" from vehicle "${plate}"?`)) return;

        try {
            await axios.delete(`${API}/driver-vehicle-mappings/${mapping._id}`, { params: { changedBy } });
            showToast('Mapping deleted successfully');
            fetchData();
            setHistoryKey(k => k + 1);
        } catch (err) {
            showToast('Failed to delete mapping', 'error');
        }
    };

    const openModal = (mapping = null) => {
        if (mapping) {
            setEditingId(mapping._id);
            setForm({
                driverId: mapping.driverId?._id || mapping.driverId,
                vehicleId: mapping.vehicleId?._id || mapping.vehicleId,
                isActive: mapping.isActive,
                notes: mapping.notes || ''
            });
        } else {
            setEditingId(null);
            setForm({
                driverId: '',
                vehicleId: '',
                isActive: true,
                notes: ''
            });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(defaultForm);
    };

    return (
        <div className="av-page">
            <style>{`
                .av-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .theme-dark .av-page { background: #0f172a; }

                /* Header */
                .av-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
                .av-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .theme-dark .av-title { color: #f1f5f9; }
                .av-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-primary {
                    background: linear-gradient(135deg, #16539C 0%, #312e81 100%);
                    box-shadow: 0 4px 12px rgba(22, 83, 156, 0.25);
                }

                /* Stats grid */
                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 28px; }
                .stat-card {
                    background: white; border-radius: 16px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.25s;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
                }
                .theme-dark .stat-card { background: #1e293b; border-color: #334155; }
                .stat-card:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.08); }
                .stat-icon-wrap {
                    width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .stat-info { display: flex; flex-direction: column; }
                .stat-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
                .stat-val { font-size: 24px; font-weight: 800; color: #1e293b; line-height: 1; }
                .theme-dark .stat-val { color: #f1f5f9; }

                /* Content Box */
                .av-content {
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); overflow: hidden;
                }
                .theme-dark .av-content { background: #1e293b; border-color: #334155; }

                /* Toolbar */
                .av-toolbar {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 18px 24px; border-bottom: 1px solid #f1f5f9;
                }
                .theme-dark .av-toolbar { border-bottom-color: #334155; }
                .av-toolbar-left h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
                .theme-dark .av-toolbar-left h3 { color: #f1f5f9; }
                
                .av-search-wrap { position: relative; display: flex; align-items: center; }
                .av-search-wrap svg { position: absolute; left: 12px; color: #94a3b8; }
                .av-search {
                    padding: 9px 12px 9px 36px; border: 1.5px solid #e2e8f0; border-radius: 10px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    width: 280px; transition: all 0.2s; font-family: 'Inter', sans-serif;
                }
                .theme-dark .av-search { background: #0f172a; border-color: #334155; color: white; }
                .av-search:focus { border-color: #16539C; box-shadow: 0 0 0 3px rgba(22, 83, 156, 0.08); }
                .av-refresh-btn {
                    border: 1.5px solid #e2e8f0; background: white; padding: 8px; border-radius: 10px;
                    cursor: pointer; color: #64748b; transition: all 0.2s; display: flex; align-items: center;
                }
                .theme-dark .av-refresh-btn { background: #1e293b; border-color: #334155; color: #94a3b8; }
                .av-refresh-btn:hover { border-color: #16539C; color: #16539C; }

                /* Table */
                .av-table-wrap { overflow-x: auto; }
                .av-table { width: 100%; border-collapse: collapse; }
                .av-table th {
                    padding: 14px 20px; text-align: left; font-size: 12px; font-weight: 600;
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0; background: #fafbfc;
                }
                .theme-dark .av-table th { background: #1a2233; color: #94a3b8; border-bottom-color: #334155; }
                .av-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .theme-dark .av-table td { border-bottom-color: #334155; }
                .av-table tr:hover { background: #fafbfc; }
                .theme-dark .av-table tr:hover { background: #242f45; }
                .av-table tr:last-child td { border-bottom: none; }

                /* Custom Cells */
                .av-user-info { display: flex; align-items: center; gap: 12px; }
                .av-avatar {
                    width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center;
                    justify-content: center; font-weight: 700; color: #16539C; background: #eef2ff;
                    font-size: 14px; border: 1px solid #c3dcf5;
                }
                .av-user-details { display: flex; flex-direction: column; }
                .av-user-name { font-weight: 700; color: #1e293b; font-size: 14px; }
                .theme-dark .av-user-name { color: #f1f5f9; }
                .av-user-sub { font-size: 12px; color: #64748b; }

                .av-vehicle-info { display: flex; align-items: center; gap: 12px; }
                .av-vehicle-avatar {
                    width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center;
                    justify-content: center; color: #059669; background: #ecfdf5;
                    border: 1px solid #a7f3d0;
                }
                .av-vehicle-details { display: flex; flex-direction: column; }
                .av-plate { font-weight: 700; color: #0f172a; font-size: 14.5px; }
                .theme-dark .av-plate { color: #f1f5f9; }
                .av-make { font-size: 12px; color: #64748b; }

                /* Badges */
                .status-badge {
                    padding: 5px 14px; border-radius: 99px; font-size: 11px; font-weight: 700;
                    display: inline-flex; align-items: center; gap: 6px; text-transform: uppercase;
                }
                .status-active { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
                .status-inactive { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }

                /* Actions */
                .av-actions { display: flex; gap: 6px; }
                .av-act-btn {
                    border: none; background: none; padding: 7px; cursor: pointer; border-radius: 8px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center;
                }
                .av-act-btn:hover { background: #f1f5f9; color: #063A5C; }
                .theme-dark .av-act-btn:hover { background: #334155; }
                .av-act-btn.delete:hover { background: #fee2e2; color: #ef4444; }

                /* Modal Styling */
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes modalPop { from { transform: scale(0.95) translateY(24px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }

                .av-modal-overlay {
                    position: fixed; inset: 0; background: rgba(8, 14, 30, 0.6);
                    backdrop-filter: blur(12px); display: flex; align-items: center; justify-content: center;
                    z-index: 1000; animation: fadeIn 0.25s ease; padding: 20px;
                }

                .av-modal {
                    background: #ffffff; border-radius: 24px; width: 100%; max-width: 580px;
                    box-shadow: 0 40px 80px -20px rgba(8, 14, 30, 0.3);
                    overflow: hidden; display: flex; flex-direction: column;
                    animation: modalPop 0.3s cubic-bezier(0.22, 1, 0.36, 1);
                }
                .theme-dark .av-modal { background: #1e293b; border: 1px solid #334155; }

                .vm-header {
                    padding: 24px 32px; display: flex; justify-content: space-between; align-items: center;
                    border-bottom: 1px solid #f1f5f9;
                }
                .theme-dark .vm-header { border-bottom-color: #334155; }
                .vm-title { font-size: 20px; font-weight: 800; color: #0f172a; margin: 0; }
                .theme-dark .vm-title { color: #f1f5f9; }
                .vm-close-btn {
                    border: none; background: #f1f5f9; cursor: pointer;
                    color: #64748b; padding: 9px; border-radius: 10px;
                    transition: all 0.2s; display: flex; align-items: center;
                }
                .theme-dark .vm-close-btn { background: #334155; color: #cbd5e1; }
                .vm-close-btn:hover { background: #fee2e2; color: #ef4444; transform: rotate(90deg); }

                .vm-body { padding: 32px; display: flex; flex-direction: column; gap: 20px; }

                .vm-field { display: flex; flex-direction: column; gap: 7px; }
                .vm-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                
                .vm-select, .vm-textarea {
                    width: 100%; padding: 13px 14px; border: 1.5px solid #e2e8f0;
                    border-radius: 12px; font-size: 14px; font-weight: 600; color: #0f172a;
                    background: #f8fafc; outline: none; transition: all 0.2s;
                    font-family: 'Inter', sans-serif; box-sizing: border-box;
                }
                .theme-dark .vm-select, .theme-dark .vm-textarea { background: #0f172a; border-color: #334155; color: white; }
                .vm-select:focus, .vm-textarea:focus { border-color: #16539C; background: #fff; box-shadow: 0 0 0 3px rgba(22, 83, 156, 0.12); }
                .theme-dark .vm-select:focus, .theme-dark .vm-textarea:focus { background: #0f172a; }

                .vm-toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: #f8fafc; border-radius: 12px; border: 1.5px solid #e2e8f0; }
                .theme-dark .vm-toggle-row { background: #0f172a; border-color: #334155; }
                .vm-toggle-info { display: flex; flex-direction: column; }
                .vm-toggle-label { font-size: 14px; font-weight: 700; color: #1e293b; }
                .theme-dark .vm-toggle-label { color: #f1f5f9; }
                .vm-toggle-sub { font-size: 12px; color: #64748b; }

                /* Toggle Switch */
                .switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                .switch input { opacity: 0; width: 0; height: 0; }
                .slider { position: absolute; cursor: pointer; inset: 0; background-color: #ccc; transition: .4s; border-radius: 24px; }
                .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
                input:checked + .slider { background-color: #16539C; }
                input:checked + .slider:before { transform: translateX(20px); }

                .vm-footer {
                    padding: 20px 32px; border-top: 1px solid #f1f5f9; display: flex;
                    justify-content: flex-end; gap: 12px; background: #fafbfc;
                }
                .theme-dark .vm-footer { background: #1e293b; border-top-color: #334155; }
                
                .vm-btn-cancel {
                    padding: 12px 24px; border: 1.5px solid #e2e8f0; background: white;
                    border-radius: 12px; font-size: 13px; font-weight: 700; color: #64748b;
                    cursor: pointer; transition: all 0.2s;
                }
                .theme-dark .vm-btn-cancel { background: #1e293b; border-color: #334155; color: #94a3b8; }
                .vm-btn-cancel:hover { border-color: #94a3b8; color: #1e293b; }
                
                .vm-btn-save {
                    padding: 12px 32px; border: none; background: linear-gradient(135deg, #16539C, #312e81);
                    color: white; border-radius: 12px; font-size: 13px; font-weight: 800;
                    cursor: pointer; transition: all 0.25s;
                    box-shadow: 0 8px 20px -4px rgba(79,70,229,0.45);
                }
                .vm-btn-save:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -6px rgba(79,70,229,0.6); }
                .vm-btn-save:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

                /* Empty state styling */
                .av-empty { text-align: center; padding: 60px 20px; color: #94a3b8; font-size: 14px; }
            `}</style>

            {toast && <div className={`av-toast ${toast.type}`}>{toast.msg}</div>}

            {/* Header */}
            <div className="av-header">
                <div>
                    <h1 className="av-title">Driver Vehicle Mapping</h1>
                    <p className="av-subtitle">Pair drivers with their designated fleet vehicles securely</p>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="premium-btn" onClick={() => openHistory()}
                        style={{ background: 'white', color: '#16539C', border: '1px solid #c7d2fe' }}>
                        <Icon d={icons.history} size={18} strokeWidth={2.2} />
                        View Assignment History
                    </button>
                    <button className="premium-btn btn-primary" onClick={() => openModal()}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Map Driver & Vehicle
                    </button>
                </div>
            </div>

            {/* Stats Row */}
            <div className="stats-grid">
                <div className="stat-card" style={{ borderLeft: '4px solid #16539C' }}>
                    <div className="stat-icon-wrap" style={{ background: '#eef2ff' }}>
                        <Icon d={icons.link} color="#16539C" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Mapped Teams</span>
                        <span className="stat-val">{stats.totalMapped}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #059669' }}>
                    <div className="stat-icon-wrap" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.link} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Active Pairs</span>
                        <span className="stat-val">{stats.activeMapped}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <div className="stat-icon-wrap" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.user} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Unmapped Drivers</span>
                        <span className="stat-val">{stats.unmappedDrivers}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #6b7280' }}>
                    <div className="stat-icon-wrap" style={{ background: '#f3f4f6' }}>
                        <Icon d={icons.truck} color="#4b5563" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Unmapped Vehicles</span>
                        <span className="stat-val">{stats.unmappedVehicles}</span>
                    </div>
                </div>
            </div>

            {/* Table Content Card */}
            <div className="av-content">
                <div className="av-toolbar">
                    <div className="av-toolbar-left">
                        <h3>Allocated Pairings</h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="av-search-wrap">
                            <Icon d={icons.search} size={16} />
                            <input
                                className="av-search"
                                placeholder="Search by driver, plate, make..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                        <button className="av-refresh-btn" onClick={fetchData} title="Refresh Data">
                            <Icon d={icons.refresh} size={16} />
                        </button>
                    </div>
                </div>

                <div className="av-table-wrap">
                    <table className="av-table">
                        <thead>
                            <tr>
                                <th>Driver Details</th>
                                <th>Vehicle Details</th>
                                <th>Assigned Date</th>
                                <th>Status</th>
                                <th>Notes</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="av-empty">Loading mappings...</td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan="6" className="av-empty">No driver-vehicle mappings set up yet.</td></tr>
                            ) : (
                                pageData.map(m => {
                                    const driverName = m.driverId?.name || 'Unknown Driver';
                                    const driverEmail = m.driverId?.email || 'N/A';
                                    const driverPhone = m.driverId?.phoneNumber || '';

                                    const plateNumber = m.vehicleId?.plateNumber || 'Unknown Vehicle';
                                    const vehicleDesc = m.vehicleId ? `${m.vehicleId.make} ${m.vehicleId.model}` : 'N/A';

                                    // Look up full driver record (with leaves) from the fetched drivers list
                                    const driverRecord = drivers.find(d => d._id === (m.driverId?._id || m.driverId));
                                    const todayStr = formatDateLocal(new Date());
                                    const activeLeaveToday = driverRecord?.leaves?.find(l =>
                                        l.status === 'Approved' &&
                                        l.startDate?.split('T')[0] <= todayStr &&
                                        l.endDate?.split('T')[0] >= todayStr
                                    );

                                    return (
                                        <tr key={m._id}>
                                            <td>
                                                <div className="av-user-info">
                                                    <div className="av-avatar">{driverName.charAt(0).toUpperCase()}</div>
                                                    <div className="av-user-details">
                                                        <div className="av-user-name" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                {driverName}
                                                                {!activeLeaveToday && m.driverId?.status === 'On Leave' && (
                                                                    <span className="status-badge" style={{ background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a' }}>On Leave</span>
                                                                )}
                                                                {!activeLeaveToday && m.isActive === false && m.driverId?.status === 'Active' && m.notes?.includes('Auto-unmapped') && (
                                                                    <span className="status-badge" style={{ background: '#e6f0fa', color: '#16539C', border: '1px solid #c3dcf5' }}>Back from Leave (Needs Vehicle)</span>
                                                                )}
                                                            </span>
                                                            {activeLeaveToday && (
                                                                <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-start', textTransform: 'uppercase' }}>
                                                                    <Icon d={icons.alert} size={10} color="white" strokeWidth={2.5} /> ON LEAVE TODAY
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="av-user-sub">{driverEmail} {driverPhone && `| ${driverPhone}`}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="av-vehicle-info">
                                                    <div className="av-vehicle-avatar">
                                                        <Icon d={icons.truck} size={18} color="#059669" />
                                                    </div>
                                                    <div className="av-vehicle-details">
                                                        <span className="av-plate">{plateNumber}{m.vehicleId?.vehicleCode && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '1px 6px' }}>{m.vehicleId.vehicleCode}</span>}</span>
                                                        <span className="av-make">{vehicleDesc}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ color: '#475569', fontSize: 13.5, fontWeight: 500 }}>
                                                {new Date(m.createdAt).toLocaleDateString(undefined, {
                                                    year: 'numeric', month: 'short', day: 'numeric'
                                                })}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${m.isActive ? 'status-active' : 'status-inactive'}`}>
                                                    {m.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={{ color: '#64748b', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {m.notes || '-'}
                                            </td>
                                            <td>
                                                <div className="av-actions">
                                                    <button className="av-act-btn" title="View Assignment History" onClick={() => {
                                                        openHistory(String(m.driverId?._id || m.driverId || ''));
                                                    }}>
                                                        <Icon d={icons.history} size={16} />
                                                    </button>
                                                    <button className="av-act-btn" title="Edit Pairing" onClick={() => openModal(m)}>
                                                        <Icon d={icons.edit} size={16} />
                                                    </button>
                                                    <button className="av-act-btn delete" title="Unmap Pairing" onClick={() => handleDelete(m)}>
                                                        <Icon d={icons.trash} size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {filtered.length > PAGE_SIZE && (
                    <div className="av-pagination">
                        <span className="av-page-info">
                            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                        </span>
                        <div className="av-page-btns">
                            <button className="av-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                <Icon d={icons.chevronLeft} size={14} /> Prev
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                <button key={p} className={`av-page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>
                                    {p}
                                </button>
                            ))}
                            <button className="av-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                Next <Icon d={icons.chevronRight} size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>


            {/* Map Modal */}
            {showModal && (
                <div className="av-modal-overlay" onClick={closeModal}>
                    <div className="av-modal" onClick={e => e.stopPropagation()}>
                        <div className="vm-header">
                            <h2 className="vm-title">{editingId ? 'Edit Mapping Configuration' : 'Create Vehicle-Driver Assignment'}</h2>
                            <button className="vm-close-btn" onClick={closeModal} title="Close">
                                <Icon d={icons.x} size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="vm-body">
                                
                                {/* Driver Field */}
                                <div className="vm-field">
                                    <label className="vm-label">Select Active Driver</label>
                                    <select
                                        className="vm-select no-icon"
                                        value={form.driverId}
                                        onChange={e => setForm({ ...form, driverId: e.target.value })}
                                        required
                                    >
                                        <option value="" disabled>-- Select a Driver --</option>
                                        
                                        {/* If editing, include the currently selected driver first */}
                                        {editingId && (() => {
                                            const currentMap = mappings.find(m => m._id === editingId);
                                            if (currentMap?.driverId) {
                                                return (
                                                    <option key={currentMap.driverId._id} value={currentMap.driverId._id}>
                                                        {currentMap.driverId.name}{currentMap.driverId.driverCode ? ` [${currentMap.driverId.driverCode}]` : ''} ({currentMap.driverId.email}) [CURRENTLY MAPPED]
                                                    </option>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {availableDrivers
                                            .filter(d => !(editingId && d._id === mappings.find(m => m._id === editingId)?.driverId?._id))
                                            .map(d => (
                                            <option key={d._id} value={d._id}>
                                                {d.name}{d.driverCode ? ` [${d.driverCode}]` : ''} ({d.email})
                                            </option>
                                        ))}
                                    </select>
                                    {availableDrivers.length === 0 && !editingId && (
                                        <span style={{ fontSize: 11.5, color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Icon d={icons.alert} size={12} color="#f59e0b" /> Note: No unmapped active drivers available.
                                        </span>
                                    )}
                                </div>

                                {/* Vehicle Field */}
                                <div className="vm-field">
                                    <label className="vm-label">Assign Fleet Vehicle</label>
                                    <select
                                        className="vm-select no-icon"
                                        value={form.vehicleId}
                                        onChange={e => setForm({ ...form, vehicleId: e.target.value })}
                                        required
                                    >
                                        <option value="" disabled>-- Select a Vehicle --</option>
                                        
                                        {/* If editing, include the currently selected vehicle first */}
                                        {editingId && (() => {
                                            const currentMap = mappings.find(m => m._id === editingId);
                                            if (currentMap?.vehicleId) {
                                                return (
                                                    <option key={currentMap.vehicleId._id} value={currentMap.vehicleId._id}>
                                                        {currentMap.vehicleId.vehicleCode ? `${currentMap.vehicleId.vehicleCode} · ` : ''}{currentMap.vehicleId.plateNumber} - {currentMap.vehicleId.make} {currentMap.vehicleId.model} [CURRENTLY MAPPED]
                                                    </option>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {availableVehicles
                                            .filter(v => !(editingId && v._id === mappings.find(m => m._id === editingId)?.vehicleId?._id))
                                            .map(v => (
                                            <option key={v._id} value={v._id}>
                                                {v.vehicleCode ? `${v.vehicleCode} · ` : ''}{v.plateNumber} ({v.make} {v.model})
                                            </option>
                                        ))}
                                    </select>
                                    {availableVehicles.length === 0 && !editingId && (
                                        <span style={{ fontSize: 11.5, color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Icon d={icons.alert} size={12} color="#f59e0b" /> Note: No unmapped active vehicles available.
                                        </span>
                                    )}
                                </div>

                                {/* Notes Field */}
                                <div className="vm-field">
                                    <label className="vm-label">Administrative Notes</label>
                                    <textarea
                                        className="vm-textarea"
                                        rows="3"
                                        placeholder="Add mapping comments, timing shifts, or specifications..."
                                        value={form.notes}
                                        onChange={e => setForm({ ...form, notes: e.target.value })}
                                    />
                                </div>

                                {/* Toggle Row */}
                                <div className="vm-toggle-row">
                                    <div className="vm-toggle-info">
                                        <span className="vm-toggle-label">Active Assigning</span>
                                        <span className="vm-toggle-sub">Temporary deactivate mapping if driver/vehicle is on leave/servicing</span>
                                    </div>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={form.isActive}
                                            onChange={e => setForm({ ...form, isActive: e.target.checked })}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>

                            </div>
                            <div className="vm-footer">
                                <button type="button" className="vm-btn-cancel" onClick={closeModal}>Cancel</button>
                                <button
                                    type="submit"
                                    className="vm-btn-save"
                                    disabled={isSaving || (!form.driverId && !editingId) || (!form.vehicleId && !editingId)}
                                >
                                    {isSaving ? 'Processing...' : editingId ? 'Update Configurations' : 'Establish Assignment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
